import { nanoid } from 'nanoid';
import { FileUploader, IUploadCompleteInfo } from '../notes/generic-notes/attachments/FileUploader';
import { arrayBufferToFile, getExtensionFromMimeType } from '../utils/Audio';
import dayjs from 'dayjs';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { IRecording, IRecordingData, ISummaryPrompt } from './RecordingState';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { ICareMapNoteTarget } from '../notes/model/ICareMapNoteTarget';
import { ICaremapNoteResponse } from '../notes/model/NotesClient';
import { Logger } from '../support/Logger';
import { NetworkAdapter } from '../support/NetworkAdapter';
import { OfflineRecordingQueue } from './OfflineRecordingQueue';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { CareMapState } from '../caremap/CareMapState';
import { RedundantRecordingStorage } from './storage/RedundantRecordingStorage';
import { AppPrincipal } from '../auth/AppPrincipal';

export const RECORDING_SERVICE_STATUS_EVENT = 'RecordingService.status-updated';
export const RECORDING_ERROR_EVENT = 'RecordingService.error';
export const RECORDING_DIRECT_UPLOAD_START = 'RecordingService.direct-upload-start';
export const RECORDING_DIRECT_UPLOAD_COMPLETE = 'RecordingService.direct-upload-complete';
export const RECORDING_DIRECT_UPLOAD_ERROR = 'RecordingService.direct-upload-error';
export const RECORDING_DIRECT_UPLOAD_RETRY = 'RecordingService.direct-upload-retry';

// Fires when a recording flow exits without committing a recording (user discarded, mic
// permission denied, paywall declined, storage check failed). Listeners self-filter using
// their own state to decide whether the abort is theirs to act on — Recorder does not
// need to know which feature initiated the recording.
export const RECORDING_ABORTED = 'RecordingService.aborted';

// Predates the 'RecordingService.*' naming above; the value is left alone because it is the
// bus key every listener already subscribes to.
export const RECORDING_SAVED_SUCCESS = 'RECORDING_SAVED_SUCCESS';

/**
 * Emitted once a recording is safely captured, whether it went straight to the server or was
 * queued for later. skipAIProcessing travels with it because whether a summary is coming decides
 * what the user is told, and reconstructing that downstream would mean guessing.
 */
export interface IRecordingSavedSuccessEvent {
    recordingId: string;
    isAutoSaved?: boolean;
    isOffline?: boolean;
    wasInterrupted?: boolean;
    missingAudioMs?: number;
    summaryPrompt?: ISummaryPrompt;
    skipAIProcessing?: boolean;
}

export interface IRecordingUploadExtras {
    skipAIProcessing?: boolean;
    appointmentId?: string;
    summaryPrompt?: ISummaryPrompt;
    autoSaved?: boolean;
    wasInterrupted?: boolean;
    missingAudioMs?: number;
    noteTarget?: ICareMapNoteTarget;
    isUpload?: boolean;
}

export class RecordingService {
    static inject = () => [
        RedundantRecordingStorage,
        FileUploader,
        BufferedEventBus,
        CaremapNotesModelFactory,
        Logger,
        NetworkAdapter,
        OfflineRecordingQueue,
        MixPanelEventEmitter,
        CareMapState,
        AppPrincipal
    ];
    constructor(
        private storage: RedundantRecordingStorage,
        private uploader: FileUploader,
        private events: BufferedEventBus,
        private notesFactory: CaremapNotesModelFactory,
        private logger: Logger,
        private networkAdapter: NetworkAdapter,
        private offlineQueue: OfflineRecordingQueue,
        private mixpanel: MixPanelEventEmitter,
        private careMapState: CareMapState,
        private principal: AppPrincipal
    ) {}

    // Map to store pending direct uploads for retry
    private pendingDirectUploads = new Map<string, {
        recording: IRecording;
        noteVM: any;
        extras: IRecordingUploadExtras;
        errorStateStartTime?: number;
    }>();

    // Track recordings currently being retried to prevent concurrent retries
    // (network can fire multiple online events in quick succession on real devices)
    private retryingDirectUploads = new Set<string>();

    /**
     * Strips data from recording that should not be stored in note's otherAttributes
     * - file: ArrayBuffer is too large for note storage
     * - careMapId: note becomes source of truth for which care map
     */
    stripRecordingDataForNote(recording: IRecording): Omit<IRecording, 'file' | 'careMapId' | 'careMapUserId' | 'userId'> {
        const stripped: any = { ...recording };
        delete stripped.file;
        delete stripped.careMapId;
        delete stripped.careMapUserId;
        delete stripped.userId;
        return stripped;
    }

    /**
     * Shared upload logic with retry. Used by both storage-based and direct upload paths.
     */
    private executeUploadWithRetry = async (params: {
        record: IRecording;
        onSuccess: (uploadedRecord: IRecording) => void;
        onAttemptFailed?: (attempt: number, error: Error) => void;
        maxAttempts?: number;
        retryDelayMs?: number;
    }): Promise<void> => {
        const { record, onSuccess, onAttemptFailed, maxAttempts = 3, retryDelayMs = 1000 } = params;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            try {
                if (!this.networkAdapter.isOnline) {
                    throw new Error('Device went offline during upload');
                }

                await new Promise<void>((resolve, reject) => {
                    this.uploader.upload(
                        [arrayBufferToFile(record.file, record.fileName, record.mimeType)],
                        (fileInfo: IUploadCompleteInfo) => {
                            const uploadedRecord: IRecording = {
                                ...record,
                                status: 'uploaded',
                                url: fileInfo.update.url || ''
                            };
                            onSuccess(uploadedRecord);
                            resolve();
                        },
                        (_failedFile: File) => reject(new Error('Upload callback reported failure')),
                        { suppressAlert: true }
                    );
                });
                return; // Success - exit the retry loop
            } catch (error) {
                onAttemptFailed?.(attempt + 1, error as Error);

                if (attempt === maxAttempts - 1) {
                    throw error; // Final attempt failed
                }
                await new Promise(r => setTimeout(r, retryDelayMs));
            }
        }
    };

    /**
     * Prepares a note view model with recording data before saving.
     */
    private prepareNoteWithRecording(noteVM: any, recording: IRecording): void {
        const note = noteVM.getNote();
        note.otherAttributes.recording = this.stripRecordingDataForNote(recording);
        noteVM.setNote(note);
    }

    // Enforces "an empty/no-speech intro note is replaced by a subsequent intro recording".
    // Called after a new intro note is durably saved, so the old empty intro is only removed
    // once the replacement is safely on the server. Uses NotesModel.deleteNote so local state
    // and subscribed UI clean up automatically.
    //
    // Intro notes with useful transcripts are preserved — once the user has captured something
    // meaningful, subsequent recordings become additive rather than replacement.
    //
    // The predicate requires a positive empty verdict (transcription === 'No speech detected.').
    // A missing transcription means "still processing, transcription failed, or pre-field" — never
    // delete on absence of evidence. In normal use every UI path that triggers a new intro recording
    // is gated to require the prior one to be fully processed (the home page's
    // hasNoRecordingsYet gate, AddCareMapButton's isProcessingIntro disable, Try Recording Again's
    // status === 'summarized' gate), so the no-transcription branch isn't expected to fire — but
    // keeping it conservative makes the predicate robust to new entry points that skip a gate.
    private cleanupOldIntroNotes = async (newNoteId: string) => {
        const activeCaremap = this.careMapState.getActiveCaremap();
        if (!activeCaremap || ('essentialMapComplete' in activeCaremap && activeCaremap.essentialMapComplete)) {
            return;
        }

        const notesModel = this.notesFactory.forActiveCaremap();
        const oldEmptyIntroNotes = notesModel.getNotes().filter(n =>
            n.otherAttributes?.recording?.summaryPrompt === 'intro'
            && n.noteId !== newNoteId
            && this.isEmptyIntroNote(n)
        );

        for (const note of oldEmptyIntroNotes) {
            try {
                await notesModel.deleteNote(note, { skipTracking: true });
            } catch (err: any) {
                this.logger.error(`Failed to delete prior empty intro note ${note.noteId}: ${err?.toString()}`);
            }
        }
    };

    private isEmptyIntroNote = (n: ICaremapNoteResponse): boolean => {
        // Backend writes 'No speech detected.' (with period) at RecordingProcessor.ts:458 when
        // Whisper hallucination is detected. That sentinel is the only positive empty verdict.
        return n.otherAttributes?.recording?.transcription === 'No speech detected.';
    };

    /**
     * Formats recording duration for analytics tracking.
     * Returns format like "2m 30s" or "45s" for durations under 1 minute.
     * Shares getDurationSeconds so the Duration string and Duration Seconds
     * numeric property can never disagree within the same event.
     */
    private formatDurationForTracking(msDuration: number): string {
        const totalSeconds = this.getDurationSeconds(msDuration);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes < 1 ? '' : minutes + 'm '}${seconds}s`;
    }

    private getDurationSeconds(msDuration: number): number {
        return Math.round(msDuration / 1000);
    }

    add = async (recording: IRecordingData, extras: IRecordingUploadExtras) => {
        const id = nanoid();
        const isOnline = this.networkAdapter.isOnline;

        // Wait for any in-flight caremap creation (e.g. from ensureCareMapExists called at recording start)
        await this.careMapState.ensureCareMapExists();

        // Capture the active care map and user context
        const userId = this.principal.userId;
        const activeCaremap = this.careMapState.getActiveCaremap();
        const careMapId = activeCaremap?.careMapId;
        // If care map is still being created (INewCareMap), it won't have userId yet — fall back to principal
        const careMapUserId: string = (activeCaremap && 'userId' in activeCaremap) ? activeCaremap.userId : userId;

        if (!careMapId || !careMapUserId || !userId) {
            throw new Error(`Cannot create recording without careMapId (${careMapId}), careMapUserId (${careMapUserId}), and userId (${userId})`);
        }

        // Don't set noteId until server-side note actually exists
        let noteVM: any = null;

        if (isOnline) {
            noteVM = this.notesFactory.createFormForNewNote(
                extras.noteTarget ?? { section: 'patient' },
                { title: 'Audio Note' }
            );
        }

        const recordToAdd: IRecording = {
            ...recording,
            id,
            url: '',
            noteId: '', // Don't set noteId until server-side note exists
            careMapId,
            userId,
            careMapUserId,
            status: isOnline ? 'recording-complete' : 'offline-pending',
            skipAIProcessing: extras.skipAIProcessing,
            summaryPrompt: extras.summaryPrompt,
            appointmentId: extras.appointmentId,
            noteTarget: extras.noteTarget,
            isUpload: extras.isUpload,
            byteLength: recording.file.byteLength,
            created: recording.createdAtOverride || dayjs().utc().toISOString(),
            createdAtOverride: recording.createdAtOverride,
            fileName: `${(new Date()).toLocaleDateString()} Recording.${getExtensionFromMimeType(recording.mimeType)}`
        };

        // Try to save to storage
        let storageFailed = false;
        try {
            await this.storage.add(recordToAdd);

            // Track that recording was saved locally
            this.mixpanel.track('Recording Saved Locally', () => ({
                'Recording Id': id,
                'Duration': this.formatDurationForTracking(recordToAdd.msDuration),
                'Duration Seconds': this.getDurationSeconds(recordToAdd.msDuration),
                'Has Appointment': Boolean(extras.appointmentId),
                'Skip AI Processing': Boolean(extras.skipAIProcessing),
                'Source': extras.isUpload ? 'Upload' : 'Microphone',
                'Is Offline': !isOnline
            }));
        } catch (storageError: any) {
            // Both IndexedDB and Filesystem failed
            storageFailed = true;
            // Tag dev flag simulation errors so Logger can filter them from Sentry
            if (storageError?.message?.includes('dev flag enabled') || storageError?.message?.includes('Simulated:')) {
                storageError.errorCode = 'dev-flag-simulation';
            }
            if (!isAutomatedTests()) {
                const devFlagsNote = storageError?.errorCode === 'dev-flag-simulation' ? ' (dev flags enabled)' : '';
                this.logger.error(`Storage failed, proceeding with direct upload${devFlagsNote}`, { storageError, recordingId: id });
            }
            this.mixpanel.track('Recording Storage Failed - Direct Upload', () => ({
                'Recording Id': id,
                'Duration': this.formatDurationForTracking(recordToAdd.msDuration),
                'Duration Seconds': this.getDurationSeconds(recordToAdd.msDuration),
                'Has Appointment': Boolean(extras.appointmentId),
                'Skip AI Processing': Boolean(extras.skipAIProcessing),
                'Source': extras.isUpload ? 'Upload' : 'Microphone',
                'Error': (storageError as Error)?.toString()
            }));
        }

        if (isOnline && noteVM) {
            if (storageFailed) {
                // Direct upload flow - no local storage
                await this.directUploadWithoutStorage(noteVM, recordToAdd, extras);
            } else {
                // Normal flow with storage
                try {
                    await this.saveNoteAndUpload(noteVM, recordToAdd, extras);
                } catch {
                    // Note save failed (e.g. API unreachable) but recording is safely stored locally
                    // as offline-pending (set by saveNoteAndUpload's catch). Enqueue for retry and
                    // notify the user their recording is secured.
                    recordToAdd.status = 'offline-pending';
                    this.offlineQueue.enqueue({
                        recordingId: recordToAdd.id,
                        action: async () => {
                            return this.createNoteAndUpload(recordToAdd, extras);
                        }
                    });
                    const savedEvent: IRecordingSavedSuccessEvent = {
                        recordingId: recordToAdd.id,
                        isAutoSaved: extras.autoSaved,
                        isOffline: true,
                        wasInterrupted: extras.wasInterrupted,
                        missingAudioMs: extras.missingAudioMs,
                        summaryPrompt: extras.summaryPrompt,
                        skipAIProcessing: recordToAdd.skipAIProcessing
                    };
                    this.events.emit(RECORDING_SAVED_SUCCESS, savedEvent);
                }
            }
        } else if (!isOnline && storageFailed) {
            // Offline AND storage failed - use direct upload flow which will store in memory for retry
            this.mixpanel.track('Storage And Network Failed - Direct Upload Pending', () => ({
                'Recording Id': id,
                'Source': extras.isUpload ? 'Upload' : 'Microphone'
            }));

            // Create a noteVM for direct upload (normally created only when online)
            const offlineNoteVM = this.notesFactory.createFormForNewNote(
                extras.noteTarget ?? { section: 'patient' },
                { title: 'Audio Note' }
            );

            // Direct upload will fail immediately (offline), but will store in pendingDirectUploads for retry
            await this.directUploadWithoutStorage(offlineNoteVM, recordToAdd, extras);
        } else {
            // Offline but storage worked - normal offline flow
            this.offlineQueue.enqueue({
                recordingId: recordToAdd.id,
                action: async () => {
                    return this.createNoteAndUpload(recordToAdd, extras);
                }
            });

            // Emit success modal event for offline recordings too
            const savedEvent: IRecordingSavedSuccessEvent = {
                recordingId: recordToAdd.id,
                isAutoSaved: extras.autoSaved,
                isOffline: true,
                wasInterrupted: extras.wasInterrupted,
                missingAudioMs: extras.missingAudioMs,
                summaryPrompt: extras.summaryPrompt,
                skipAIProcessing: recordToAdd.skipAIProcessing
            };
            this.events.emit(RECORDING_SAVED_SUCCESS, savedEvent);
        }

        return recordToAdd;
    };

    private createNoteAndUpload = async (recording: IRecording, extras: IRecordingUploadExtras) => {
        // Create fresh note. Prefer the target persisted on the recording so an offline retry after
        // an app restart (where `extras` may be re-derived) still lands on the right person.
        const noteVM = this.notesFactory.createFormForNewNote(
            recording.noteTarget ?? extras.noteTarget ?? { section: 'patient' },
            { title: 'Audio Note' }
        );

        // Set recording in the note's otherAttributes before saving
        this.prepareNoteWithRecording(noteVM, recording);

        try {
            await noteVM.save({
                trackingFrom: 'Offline Recording',
                skipToast: true
            });

            if (extras.summaryPrompt === 'intro') {
                await this.cleanupOldIntroNotes(noteVM.getNote().noteId);
            }

            // Only set noteId after successful server-side save
            const updatedRecording: IRecording = {
                ...recording,
                noteId: noteVM.getNote().noteId,
                status: 'recording-complete'
            };

            // Emit success modal event instead of showing toast
            const savedEvent: IRecordingSavedSuccessEvent = {
                recordingId: recording.id,
                isAutoSaved: extras.autoSaved,
                wasInterrupted: extras.wasInterrupted,
                missingAudioMs: extras.missingAudioMs,
                summaryPrompt: extras.summaryPrompt,
                skipAIProcessing: recording.skipAIProcessing
            };
            this.events.emit(RECORDING_SAVED_SUCCESS, savedEvent);

            // Update in IndexedDB with real noteId
            await this.storage.update(updatedRecording);

            // Track successful offline recording sync
            this.mixpanel.track('Offline Recording Synced', () => ({
                'Duration': this.formatDurationForTracking(updatedRecording.msDuration),
                'Duration Seconds': this.getDurationSeconds(updatedRecording.msDuration),
                'Has Appointment': Boolean(updatedRecording.appointmentId),
                'Skip AI Processing': Boolean(updatedRecording.skipAIProcessing),
                'Source': updatedRecording.isUpload ? 'Upload' : 'Microphone'
            }));

            // Start upload after note is saved and recording is updated
            this.upload(updatedRecording);
        } catch (error) {
            // If note save fails, revert to offline-pending to retry later
            const failedRecording: IRecording = {...recording, status: 'offline-pending'};
            await this.storage.update(failedRecording);
            throw error;
        }
    };

    private saveNoteAndUpload = async (noteVM: any, recording: IRecording, extras: IRecordingUploadExtras) => {
        // Set recording in the note's otherAttributes before saving
        this.prepareNoteWithRecording(noteVM, recording);

        try {
            await noteVM.save({
                trackingFrom: 'Recording',
                skipToast: true
            });

            if (extras.summaryPrompt === 'intro') {
                await this.cleanupOldIntroNotes(noteVM.getNote().noteId);
            }

            // Only set noteId after successful server-side save
            const updatedRecording: IRecording = {
                ...recording,
                noteId: noteVM.getNote().noteId
                // Keep status as 'recording-complete' since it was already set for online recordings
            };

            // Emit success modal event instead of showing toast
            const savedEvent: IRecordingSavedSuccessEvent = {
                recordingId: recording.id,
                isAutoSaved: extras.autoSaved,
                wasInterrupted: extras.wasInterrupted,
                missingAudioMs: extras.missingAudioMs,
                summaryPrompt: extras.summaryPrompt,
                skipAIProcessing: recording.skipAIProcessing
            };
            this.events.emit(RECORDING_SAVED_SUCCESS, savedEvent);

            // Update in IndexedDB with real noteId
            await this.storage.update(updatedRecording);

            // Start upload after note is saved and recording is updated
            this.upload(updatedRecording);
        } catch (error) {
            // If note save fails, revert to offline-pending to retry later
            const failedRecording: IRecording = {...recording, status: 'offline-pending'};
            await this.storage.update(failedRecording);
            throw error;
        }
    };

    /**
     * Direct upload flow when both IndexedDB and Filesystem storage fail.
     * Uploads recording directly to cloud without local persistence.
     */
    private directUploadWithoutStorage = async (
        noteVM: any,
        recording: IRecording,
        extras: IRecordingUploadExtras
    ) => {
        // Track direct upload started
        this.mixpanel.track('Direct Upload Started', () => ({
            'Recording Id': recording.id,
            'Duration': this.formatDurationForTracking(recording.msDuration),
            'Duration Seconds': this.getDurationSeconds(recording.msDuration),
            'Has Appointment': Boolean(recording.appointmentId),
            'Skip AI Processing': Boolean(recording.skipAIProcessing)
        }));

        // Emit event to show non-dismissable modal in uploading state
        this.events.emit(RECORDING_DIRECT_UPLOAD_START, {
            recordingId: recording.id
        });

        // Track whether note was saved (for retry logic)
        let updatedRecording: IRecording = recording;

        try {
            // Set recording in note's otherAttributes before saving
            this.prepareNoteWithRecording(noteVM, recording);

            // Save note to get noteId
            await noteVM.save({
                trackingFrom: 'Direct Upload Recording',
                skipToast: true
            });

            if (extras.summaryPrompt === 'intro') {
                await this.cleanupOldIntroNotes(noteVM.getNote().noteId);
            }

            // Note saved successfully - update recording with noteId
            updatedRecording = {
                ...recording,
                noteId: noteVM.getNote().noteId,
                status: 'uploading'
            };

            // Upload directly (no storage update since storage failed)
            await this.uploadDirectly(updatedRecording);

            // Track successful direct upload
            this.mixpanel.track('Direct Upload Completed', () => ({
                'Recording Id': recording.id,
                'Duration Seconds': this.getDurationSeconds(recording.msDuration),
                'Retry Count': 0
            }));

            // Emit completion event - modal will close
            this.events.emit(RECORDING_DIRECT_UPLOAD_COMPLETE, {
                recordingId: recording.id,
                success: true
            });

        } catch (error) {
            this.logger.error('Direct upload failed', { error, recordingId: recording.id });

            // Track failure
            this.mixpanel.track('Direct Upload Error State Shown', () => ({
                'Recording Id': recording.id,
                'Error': (error as Error)?.toString()
            }));

            // Store pending upload data for retry
            // Use updatedRecording which has noteId if note was saved before upload failed
            this.pendingDirectUploads.set(recording.id, {
                recording: updatedRecording,
                noteVM,
                extras,
                errorStateStartTime: Date.now()
            });

            // Emit error event - modal transforms to error state with retry option
            this.events.emit(RECORDING_DIRECT_UPLOAD_ERROR, {
                recordingId: recording.id,
                error: (error as Error)?.toString()
            });
        }
    };

    /**
     * Upload recording directly without touching storage.
     * Uses shared retry logic but doesn't update storage.
     */
    private uploadDirectly = async (record: IRecording): Promise<void> => {
        await this.executeUploadWithRetry({
            record,
            onSuccess: (uploadedRecord) => {
                // Emit status event (note will be updated by RecordingState listener)
                this.events.emit(RECORDING_SERVICE_STATUS_EVENT, uploadedRecord);
            },
            onAttemptFailed: (attempt, error) => {
                this.mixpanel.track('Direct Upload Failed', () => ({
                    'Recording Id': record.id,
                    'Error': error?.toString(),
                    'Attempt Number': attempt
                }));
            }
        });
    };

    /**
     * Retry a pending direct upload (called by user clicking retry or network restore).
     */
    retryDirectUpload = async (recordingId: string, isAutoRetry: boolean = false) => {
        // Prevent concurrent retries for the same recording
        // (network can fire multiple online events in quick succession on real devices)
        if (this.retryingDirectUploads.has(recordingId)) {
            return;
        }

        const pendingUpload = this.pendingDirectUploads.get(recordingId);
        if (!pendingUpload) {
            this.logger.error('No pending direct upload found for retry', { recordingId });
            return;
        }

        this.retryingDirectUploads.add(recordingId);

        try {
            const { recording, noteVM, errorStateStartTime } = pendingUpload;

            // Track retry
            const timeInErrorState = errorStateStartTime ? Date.now() - errorStateStartTime : 0;
            this.mixpanel.track(isAutoRetry ? 'Direct Upload Auto Retry' : 'Direct Upload Manual Retry', () => ({
                'Recording Id': recordingId,
                'Time In Error State Ms': timeInErrorState
            }));

            // Emit event to transform modal back to uploading state
            this.events.emit(RECORDING_DIRECT_UPLOAD_RETRY, {
                recordingId: recording.id
            });

            try {
                // Only save note if it wasn't already saved (noteId is empty)
                // If note was saved but upload failed, recording.noteId will already be set
                if (!recording.noteId) {
                    this.prepareNoteWithRecording(noteVM, recording);
                    await noteVM.save({
                        trackingFrom: 'Direct Upload Recording Retry',
                        skipToast: true
                    });
                }

                const updatedRecording: IRecording = {
                    ...recording,
                    noteId: recording.noteId || noteVM.getNote().noteId,
                    status: 'uploading'
                };

                await this.uploadDirectly(updatedRecording);

                // Success - clean up and close modal
                this.pendingDirectUploads.delete(recordingId);

                // Track successful retry
                this.mixpanel.track('Direct Upload Completed', () => ({
                    'Recording Id': recording.id,
                    'Duration Seconds': this.getDurationSeconds(recording.msDuration),
                    'Retry Count': 1
                }));

                this.events.emit(RECORDING_DIRECT_UPLOAD_COMPLETE, {
                    recordingId: recording.id,
                    success: true
                });

            } catch (error) {
                // Still failing - update error state start time and stay in error state
                this.pendingDirectUploads.set(recordingId, {
                    ...pendingUpload,
                    errorStateStartTime: Date.now()
                });

                this.mixpanel.track('Direct Upload Error State Shown', () => ({
                    'Recording Id': recording.id,
                    'Error': (error as Error)?.toString()
                }));

                this.events.emit(RECORDING_DIRECT_UPLOAD_ERROR, {
                    recordingId: recording.id,
                    error: (error as Error)?.toString()
                });
            }
        } finally {
            this.retryingDirectUploads.delete(recordingId);
        }
    };

    /**
     * Check if there are any pending direct uploads waiting for retry.
     */
    hasPendingDirectUploads = (): boolean => {
        return this.pendingDirectUploads.size > 0;
    };

    /**
     * Retry all pending direct uploads (called on network restore).
     */
    retryAllPendingDirectUploads = (): void => {
        for (const recordingId of this.pendingDirectUploads.keys()) {
            this.retryDirectUpload(recordingId, true);
        }
    };

    get = async (id: string) => {
        return await this.storage.get(id);
    };

    getAll = async () => {
        return await this.storage.getAll();
    };

    /**
     * Check if storage is available for recording.
     * Throws IndexedDBConnectionError if no storage backend is available.
     */
    checkStorageConnection = async (): Promise<void> => {
        return await this.storage.checkStorageConnection();
    };

    delete = (id: string) => {
        this.storage.delete(id);
    };

    upload = async (record: IRecording) => {
        // Skip if this is a pending direct upload - those are handled separately
        // by retryAllPendingDirectUploads() to avoid duplicate retry attempts
        if (this.pendingDirectUploads.has(record.id)) {
            return;
        }

        // Check network status before attempting upload
        if (!this.networkAdapter.isOnline) {
            // Queue for later if offline
            this.offlineQueue.enqueue({
                recordingId: record.id,
                action: () => this.upload(record)
            });
            return;
        }

        const newRecord: IRecording = { ...record, status: 'uploading' };

        await this.storage.update(newRecord);
        this.events.emit(RECORDING_SERVICE_STATUS_EVENT, newRecord);

        try {
            await this.executeUploadWithRetry({
                record: newRecord,
                onSuccess: (uploadedRecord) => {
                    this.storage.update(uploadedRecord);
                    this.events.emit(RECORDING_SERVICE_STATUS_EVENT, uploadedRecord);
                }
            });
        } catch (error) {
            // If network error, queue for retry when online
            const errorMessage = (error as Error).message || '';
            if (errorMessage.includes('offline') || errorMessage.includes('network')) {
                this.offlineQueue.enqueue({
                    recordingId: record.id,
                    action: () => this.upload(record)
                });
                return;
            }

            // Update record status to upload-failed
            const failedRecord: IRecording = { ...record, status: 'upload-failed' };
            await this.storage.update(failedRecord);
            this.events.emit(RECORDING_SERVICE_STATUS_EVENT, failedRecord);

            // Log to Sentry
            this.logger.error('failed to upload recording after 3 attempts');

            // Emit error event to show modal
            this.events.emit(RECORDING_ERROR_EVENT, {
                recordingId: record.id,
                errorType: 'upload-failed',
                title: 'Unable to Upload',
                message: 'Your recording is safely saved on your device and will automatically upload once your connection improves.'
            });
        }
    };

    // New method to retry failed uploads
    retryUpload = async (recordingId: string) => {
        // Skip if this is a pending direct upload - those are handled separately
        // by retryAllPendingDirectUploads() to avoid duplicate retry attempts
        if (this.pendingDirectUploads.has(recordingId)) {
            return;
        }

        const recording = await this.get(recordingId);

        if (!recording) {
            this.logger.error('Recording not found for retry', { recordingId });
            return;
        }

        if (recording && (recording.status === 'upload-failed' || recording.status === 'offline-pending' || recording.status === 'uploading')) {
            if (!recording.noteId || recording.noteId === '') {
                // Need to create a note first since noteId isn't set
                await this.createNoteAndUpload(recording, {
                    skipAIProcessing: recording.skipAIProcessing,
                    appointmentId: recording.appointmentId,
                    summaryPrompt: recording.summaryPrompt
                });
            } else {
                // Just retry upload since note already exists
                await this.upload(recording);
            }
        } else if (recording) {
            // Don't log this error when automated tests are running
            if (!isAutomatedTests()) {
                this.logger.error('Recording not eligible for retry', { recording });
            }
        }
    };

    // Get offline recordings
    getOfflineRecordings = async (): Promise<IRecording[]> => {
        const allRecordings = await this.getAll();
        return allRecordings.filter(r => r.status === 'offline-pending');
    };

    // Dev tool: Add test recording with specific status
    addTestRecording = async (params: {
        status: IRecording['status'];
        mimeType?: string;
        msDuration?: number;
        transcription?: string;
        skipAIProcessing?: boolean;
        shouldShowErrorModal?: boolean;
    }) => {
        const id = nanoid();
        const activeCaremap = this.careMapState.getActiveCaremap();
        const careMapId = activeCaremap?.careMapId || '';
        const careMapUserId: string = (activeCaremap && 'userId' in activeCaremap) ? activeCaremap.userId : this.principal.userId;
        const userId = this.principal.userId;

        // Create a real note first so the recording appears in the UI
        const noteVM = this.notesFactory.createFormForNewNote(
            { section: 'patient' },
            { title: 'Test Audio Note' }
        );

        // Create minimal fake audio data
        const fakeAudioData = new ArrayBuffer(100);

        const testRecording: IRecording = {
            id,
            fileName: `Test Recording ${Date.now()}.${params.mimeType === 'audio/m4a' ? 'm4a' : 'mp4'}`,
            file: fakeAudioData,
            url: params.status === 'uploaded' ? 'https://example.com/test.mp4' : '',
            noteId: '',
            status: params.status,
            careMapId,
            careMapUserId,
            userId,
            mimeType: params.mimeType || 'audio/mp4',
            msDuration: params.msDuration || 120000,
            byteLength: fakeAudioData.byteLength,
            created: dayjs().utc().toISOString(),
            transcription: params.transcription,
            skipAIProcessing: params.skipAIProcessing ?? true
        };

        // Set recording in note's otherAttributes before saving
        this.prepareNoteWithRecording(noteVM, testRecording);

        // Save the note
        await noteVM.save({
            trackingFrom: 'Test Recording',
            skipToast: true
        });

        // Update recording with real noteId
        testRecording.noteId = noteVM.getNote().noteId;

        // Save to IndexedDB
        await this.storage.add(testRecording);

        // Emit status event to update UI
        this.events.emit(RECORDING_SERVICE_STATUS_EVENT, testRecording);

        // If requested and status is an error status, emit error event after delay
        // to allow the test recording modal to close first
        if (params.shouldShowErrorModal && this.isErrorStatus(params.status)) {
            setTimeout(() => {
                this.emitErrorModalForStatus(params.status, testRecording.id);
            }, 350); // Ionic modal animation duration
        }

        return testRecording;
    };

    private isErrorStatus(status: IRecording['status']): boolean {
        return status === 'upload-failed' ||
               status === 'transcribe-failed' ||
               status === 'summarize-failed';
    }

    private emitErrorModalForStatus(status: IRecording['status'], recordingId: string) {
        const errorMessages: Record<string, { errorType: string; title: string; message: string }> = {
            'upload-failed': {
                errorType: 'upload-failed',
                title: 'Unable to Upload',
                message: 'Your recording is safely saved on your device and will automatically upload once your connection improves.'
            },
            'transcribe-failed': {
                errorType: 'processing-failed',
                title: 'Processing Issue',
                message: 'Innerhive ran into a problem while processing your recording. Please try hard closing and relaunching the app to try again, or contact us for help troubleshooting.'
            },
            'summarize-failed': {
                errorType: 'processing-failed',
                title: 'Processing Issue',
                message: 'Innerhive ran into a problem while processing your recording. Please try hard closing and relaunching the app to try again, or contact us for help troubleshooting.'
            }
        };

        const errorData = errorMessages[status];
        if (errorData) {
            this.events.emit(RECORDING_ERROR_EVENT, {
                recordingId,
                ...errorData
            });
        }
    }
};
